// components/RichTextEditor/editorConfig.ts
// Quill editor configuration and toolbar setup

/**
 * Custom toolbar configuration for Quill
 * Defines which formatting options appear in the toolbar
 */
export const TOOLBAR_CONFIG = [
    // Text formatting group
    [{ 'header': [1, 2, 3, false] }], // H1, H2, H3, Normal

    // Font styling group
    ['bold', 'italic', 'underline', 'strike'],

    // Text color and background
    [{ 'color': [] }, { 'background': [] }],

    // Lists
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],

    // Text alignment
    [{ 'align': [] }],

    // Links and images
    ['link', 'image'],

    // Video embeds (YouTube, Vimeo)
    ['video'],

    // Blockquotes
    ['blockquote'],

    // Clear formatting
    ['clean'],
];

/**
 * Simplified toolbar for basic formatting
 * Use this for less experienced users
 */
export const SIMPLE_TOOLBAR_CONFIG = [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link', 'image'],
    ['clean'],
];

/**
 * Kid-friendly color palette
 * Curated colors that work well for ages 8-14
 */
export const KID_FRIENDLY_COLORS = [
    // Primary colors
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#FFA07A', // Orange
    '#98D8C8', // Mint
    '#F7DC6F', // Yellow
    '#BB8FCE', // Purple
    '#85C1E2', // Sky Blue

    // Neutrals
    '#2C3E50', // Dark Gray
    '#7F8C8D', // Gray
    '#FFFFFF', // White
    '#000000', // Black
];

/**
 * Quill module configuration
 * Customizes editor behavior
 */
export const QUILL_MODULES = {
    toolbar: TOOLBAR_CONFIG,

    clipboard: {
        // Better handling of pasted content
        matchVisual: false,
    },

    history: {
        delay: 1000,
        maxStack: 50,
        userOnly: true,
    },
};

/**
 * Quill formats - allowed formatting types
 */
export const QUILL_FORMATS = [
    // Text
    'header',
    'bold', 'italic', 'underline', 'strike',

    // Color
    'color', 'background',

    // Lists
    'list', 'bullet',

    // Alignment
    'align',

    // Indentation
    'indent',

    // Links and media
    'link', 'image', 'video',

    // Blockquotes
    'blockquote',

    // Code blocks (optional, can be removed for kids)
    'code-block',
];

/**
 * Editor placeholder text
 */
export const EDITOR_PLACEHOLDER = 'Start writing your article here... Share tips, insights, and inspiration for young writers!';

/**
 * Custom bounds for the editor
 * Helps with positioning the toolbar
 */
export const getEditorBounds = (): string | HTMLElement => {
    const element = document.querySelector('.article-editor-container');
    // Cast to HTMLElement to satisfy TypeScript
    return (element as HTMLElement) || 'body';
};

/**
 * Character limits for different content types
 */
export const CONTENT_LIMITS = {
    minWords: 50,
    maxWords: 5000,
    minCharacters: 100,
    maxCharacters: 30000,
};

/**
 * Image upload configuration
 */
export const IMAGE_CONFIG = {
    maxSize: 2 * 1024 * 1024, // 2MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    maxWidth: 1200,
    maxHeight: 1200,
};

/**
 * Video embed configuration
 * Allowed video platforms
 */
export const VIDEO_PLATFORMS = {
    youtube: {
        regex: /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
        embedUrl: (id: string) => `https://www.youtube.com/embed/${id}`,
    },
    vimeo: {
        regex: /vimeo\.com\/(\d+)/,
        embedUrl: (id: string) => `https://player.vimeo.com/video/${id}`,
    },
};

/**
 * Keyboard shortcuts configuration
 */
export const KEYBOARD_SHORTCUTS = {
    bold: { key: 'B', ctrlKey: true },
    italic: { key: 'I', ctrlKey: true },
    underline: { key: 'U', ctrlKey: true },
    undo: { key: 'Z', ctrlKey: true },
    redo: { key: 'Y', ctrlKey: true },
};

/**
 * Editor theme configuration
 */
export const EDITOR_THEME = 'snow'; // Options: 'snow' (toolbar) or 'bubble' (inline)

/**
 * Default editor styles
 */
export const EDITOR_STYLES = {
    minHeight: '300px',
    maxHeight: '600px',
    fontSize: '16px',
    lineHeight: '1.6',
};

/**
 * Auto-save configuration
 */
export const AUTO_SAVE_CONFIG = {
    enabled: true,
    intervalMs: 30000, // 30 seconds
    debounceMs: 2000, // 2 seconds debounce on typing
};

/**
 * Validation rules for content
 */
export const VALIDATION_RULES = {
    title: {
        minLength: 5,
        maxLength: 100,
        required: true,
    },
    content: {
        minWords: 50,
        maxWords: 5000,
        required: true,
    },
};

/**
 * Error messages
 */
export const ERROR_MESSAGES = {
    contentTooShort: 'Article must be at least 50 words',
    contentTooLong: 'Article cannot exceed 5000 words',
    titleRequired: 'Please enter a title',
    titleTooShort: 'Title must be at least 5 characters',
    titleTooLong: 'Title cannot exceed 100 characters',
    imageTooLarge: 'Image must be less than 2MB',
    invalidImageType: 'Only JPG, PNG, GIF, and WebP images are allowed',
    uploadFailed: 'Failed to upload image. Please try again.',
};

/**
 * Success messages
 */
export const SUCCESS_MESSAGES = {
    articlePublished: 'Article published successfully!',
    articleUpdated: 'Article updated successfully!',
    draftSaved: 'Draft saved successfully!',
    imageUploaded: 'Image uploaded successfully!',
};

/**
 * Get custom Quill configuration based on user preferences
 * @param options - Configuration options
 * @returns Customized Quill configuration
 */
export const getQuillConfig = (options: {
    simple?: boolean;
    placeholder?: string;
}) => {
    return {
        theme: EDITOR_THEME,
        placeholder: options.placeholder || EDITOR_PLACEHOLDER,
        modules: {
            ...QUILL_MODULES,
            toolbar: options.simple ? SIMPLE_TOOLBAR_CONFIG : TOOLBAR_CONFIG,
        },
        formats: QUILL_FORMATS,
        bounds: getEditorBounds(),
    };
};

// Named exports for better tree-shaking
const editorConfig = {
    TOOLBAR_CONFIG,
    SIMPLE_TOOLBAR_CONFIG,
    KID_FRIENDLY_COLORS,
    QUILL_MODULES,
    QUILL_FORMATS,
    EDITOR_PLACEHOLDER,
    CONTENT_LIMITS,
    IMAGE_CONFIG,
    VIDEO_PLATFORMS,
    KEYBOARD_SHORTCUTS,
    EDITOR_THEME,
    EDITOR_STYLES,
    AUTO_SAVE_CONFIG,
    VALIDATION_RULES,
    ERROR_MESSAGES,
    SUCCESS_MESSAGES,
    getQuillConfig,
};

export default editorConfig;